import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { COUNTRIES, countryByIso } from "@/lib/countries";

const SOURCES = ["Instagram", "TikTok", "A friend", "Google", "Other"];

const inputCls = "rounded-none h-12 border-ink/20 focus-visible:ring-flame";
const labelCls = "text-xs font-bold uppercase tracking-widest";
const countryOptions = COUNTRIES.map((c) => ({ id: c.name, search: c.search, ...c }));
const dialOptions = COUNTRIES.map((c) => ({ id: c.iso, search: c.search, ...c }));

export const WaitlistForm = ({ trips = [], events = [], initialTrip = "", lockTrip = false, heading = "Join the Waitlist" }) => {
  const blank = {
    first_name: "", last_name: "", email: "", phone: "", phone_iso: "US", age: "", city: "", country: "",
    instagram: "", source: "", consent: false, trip_slug: initialTrip, message: "",
  };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const lockedTrip = trips.find((t) => t.slug === initialTrip);
  const lockedEvent = events.find((e) => `irl:${e.slug}` === initialTrip);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country) {
      toast.error("Please select your country.");
      return;
    }
    if (!form.consent) {
      toast.error("Please check the consent box so we can contact you.");
      return;
    }
    const ageNum = parseInt(form.age, 10);
    if (isNaN(ageNum) || ageNum < 18 || ageNum > 45) {
      toast.error("Thanks for your interest! Roamies trips are currently for travelers ages 18–45, so this waitlist isn't the right fit right now — we'd still love to stay in touch.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", {
        ...form,
        phone_code: countryByIso(form.phone_iso)?.dial || "+1",
      });
      toast.success(data?.message || "You're on the list! Check your inbox for a confirmation.");
      setForm(blank);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="wl-first" className={labelCls}>First name</Label>
          <Input id="wl-first" data-testid="waitlist-first-name-input" required value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Alex" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wl-last" className={labelCls}>Last name</Label>
          <Input id="wl-last" data-testid="waitlist-last-name-input" required value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Kim" className={inputCls} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="wl-email" className={labelCls}>Email</Label>
        <Input id="wl-email" type="email" data-testid="waitlist-email-input" required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="wl-age" className={labelCls}>Age</Label>
          <Input id="wl-age" type="number" min="18" max="45" data-testid="waitlist-age-input" required value={form.age}
            onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="18–45" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wl-city" className={labelCls}>Home city</Label>
          <Input id="wl-city" data-testid="waitlist-city-input" required value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Austin, TX" className={inputCls} />
        </div>
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label className={labelCls}>Country</Label>
          <SearchSelect selectedIso={form.country} onSelect={(v) => setForm({ ...form, country: v })}
            placeholder="Select country" testid="waitlist-country-input" options={countryOptions}
            renderSelected={(o) => <span>{o.name}</span>}
            renderOption={(o) => <span>{o.name}</span>} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className={labelCls}>Phone (optional)</Label>
          <div className="flex gap-2">
            <div className="w-24 shrink-0">
              <SearchSelect selectedIso={form.phone_iso} onSelect={(v) => setForm({ ...form, phone_iso: v })}
                placeholder="+1" testid="waitlist-phone-code" options={dialOptions}
                renderSelected={(o) => <span>{o.dial}</span>}
                renderOption={(o) => <span className="flex justify-between w-full gap-2"><span className="truncate">{o.name}</span><span className="text-ink/40 shrink-0">{o.dial}</span></span>} />
            </div>
            <Input type="tel" inputMode="tel" data-testid="waitlist-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="555 555 5555" className={`${inputCls} flex-1`} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="wl-ig" className={labelCls}>Instagram (optional)</Label>
          <Input id="wl-ig" data-testid="waitlist-instagram-input" value={form.instagram}
            onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@yourhandle" className={inputCls} />
        </div>
      </div>

      {lockTrip && (lockedTrip || lockedEvent) ? (
        <div className="space-y-2">
          <Label className={labelCls}>You're joining for</Label>
          <p data-testid="waitlist-locked-trip" className="h-12 flex items-center px-3 border border-ink/20 bg-cream font-bold text-sm">
            {lockedTrip ? `${lockedTrip.title} — ${lockedTrip.destination}` : `${lockedEvent.title} — ${lockedEvent.city}`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className={labelCls}>Trip or event you're eyeing (optional)</Label>
          <Select
            value={form.trip_slug || "any"}
            onValueChange={(v) => setForm({ ...form, trip_slug: v === "any" ? "" : v })}
          >
            <SelectTrigger data-testid="waitlist-trip-select" className="rounded-none h-12 border-ink/20">
              <SelectValue placeholder="Any drop — surprise me" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any" data-testid="waitlist-trip-option-any">Any drop — surprise me</SelectItem>
              {trips.map((t) => (
                <SelectItem key={t.slug} value={t.slug} data-testid={`waitlist-trip-option-${t.slug}`}>
                  {t.title} — {t.destination}
                </SelectItem>
              ))}
              {events.map((ev) => (
                <SelectItem key={ev.slug} value={`irl:${ev.slug}`} data-testid={`waitlist-event-option-${ev.slug}`}>
                  IRL: {ev.title} — {ev.city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label className={labelCls}>How did you hear about Roamies?</Label>
        <Select value={form.source || "unset"} onValueChange={(v) => setForm({ ...form, source: v === "unset" ? "" : v })}>
          <SelectTrigger data-testid="waitlist-source-select" className="rounded-none h-12 border-ink/20">
            <SelectValue placeholder="Pick one" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unset" data-testid="waitlist-source-option-none">Prefer not to say</SelectItem>
            {SOURCES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`waitlist-source-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="wl-message" className={labelCls}>Anything else? (optional)</Label>
        <Textarea id="wl-message" data-testid="waitlist-message-input" value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Questions, dietary stuff, bringing a friend..."
          className="rounded-none border-ink/20 focus-visible:ring-flame min-h-[80px]" />
      </div>

      <label className="flex items-start gap-3 text-xs text-ink/70 cursor-pointer" data-testid="waitlist-consent-label">
        <Checkbox
          checked={form.consent}
          onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
          data-testid="waitlist-consent-checkbox"
          className="rounded-none border-ink/30 data-[state=checked]:bg-flame data-[state=checked]:border-flame mt-0.5"
        />
        I agree to be contacted by Roamies about trips and events, and I confirm I'm between 18 and 45.
      </label>

      <Button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit-btn"
        className="w-full bg-flame hover:bg-flame/90 text-white rounded-none h-14 font-bold uppercase tracking-[0.2em] text-sm transition-transform hover:-translate-y-0.5"
      >
        {loading ? "Joining..." : heading}
      </Button>
      <p className="text-xs text-ink/50">No spam, no payment now. We'll email you first when a drop opens.</p>
    </form>
  );
};

